// =============================================================================
// VandieZ admin-web -- UserPicker
// =============================================================================
// Replaces raw "paste a user UUID" inputs across the admin. Hits
// /admin/users?q=… (debounced 250ms) and lets the operator pick from
// the dropdown by phone/name. The parent gets the canonical user_id
// back via `onChange(userId, summary)` so existing payload code stays
// unchanged.
//
// Constraints honoured:
//   * No external state management — the picker manages its own search
//     buffer + result list locally.
//   * No new dependencies — debounce is a plain setTimeout.
//   * Closes on outside click + Escape so the operator can dismiss
//     without selecting.
// =============================================================================

import { Loader2, Search, X } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { usersAdminApi } from "@/api/client";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { AdminUserSummary } from "@/types/rbac";

interface UserPickerProps {
  value: AdminUserSummary | null;
  onChange: (user: AdminUserSummary | null) => void;
  placeholder?: string;
  /** Restrict the search to one role — passed to the backend's primary_role filter. */
  roleFilter?: "shipper" | "driver" | "owner" | "agent" | "admin";
  disabled?: boolean;
  /** ID for the wrapper (so the parent <Label htmlFor=…> still works). */
  id?: string;
}

export function UserPicker({
  value,
  onChange,
  placeholder = "Search by phone or name…",
  roleFilter,
  disabled,
  id,
}: UserPickerProps): React.JSX.Element {
  const { accessToken } = useAuth();
  const [query, setQuery] = useState<string>("");
  const [results, setResults] = useState<AdminUserSummary[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | null>(null);

  // Debounced search.  Only runs when the picker is open and the query
  // has 2+ chars (any shorter is too noisy and pulls the whole user table).
  const runSearch = useCallback(
    (q: string) => {
      if (accessToken === null) {return;}
      if (q.trim().length < 2) {
        setResults([]);
        return;
      }
      setLoading(true);
      setError(null);
      usersAdminApi
        .search(accessToken, {
          q: q.trim(),
          limit: 10,
          offset: 0,
          ...(roleFilter !== undefined ? { primary_role: roleFilter } : {}),
        })
        .then((res) => { setResults(res.items); })
        .catch((e: unknown) => {
          if (e instanceof ApiError) {
            setError(e.problem.detail);
          } else {
            setError("Search failed. Try again.");
          }
          setResults([]);
        })
        .finally(() => { setLoading(false); });
    },
    [accessToken, roleFilter],
  );

  useEffect(() => {
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      runSearch(query);
    }, 250);
    return () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [query, runSearch]);

  // Click-outside / Escape closes the dropdown without changing selection.
  useEffect(() => {
    if (!open) {return;}
    const onClick = (e: MouseEvent): void => {
      if (
        wrapperRef.current !== null &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {setOpen(false);}
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Selected mode: show a tag-style summary with a clear button.
  if (value !== null) {
    return (
      <div
        id={id}
        className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm"
      >
        <div className="flex-1">
          <div className="font-medium">{value.full_name}</div>
          <div className="text-xs text-muted-foreground font-mono">
            {value.phone} · {value.primary_role}
          </div>
        </div>
        <button
          type="button"
          aria-label="Clear selection"
          disabled={disabled}
          onClick={() => {
            onChange(null);
            setQuery("");
            setResults([]);
            setOpen(false);
          }}
          className="rounded-full p-1 text-muted-foreground hover:bg-muted disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative" id={id}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => { setOpen(true); }}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-8"
          autoComplete="off"
        />
        {loading && (
          <Loader2 className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {open && (query.trim().length >= 2 || results.length > 0) && (
        <div className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-md border bg-popover shadow-lg">
          {error !== null ? (
            <div className="px-3 py-2 text-xs text-danger-subtle-foreground">{error}</div>
          ) : results.length === 0 && !loading ? (
            <div className="px-3 py-3 text-center text-xs text-muted-foreground">
              {query.trim().length < 2
                ? "Type at least 2 characters."
                : "No users match."}
            </div>
          ) : (
            <ul className="divide-y">
              {results.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    className={cn(
                      "block w-full px-3 py-2 text-left hover:bg-muted",
                      "focus:bg-muted focus:outline-none",
                    )}
                    onClick={() => {
                      onChange(u);
                      setOpen(false);
                      setQuery("");
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{u.full_name}</span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs capitalize">
                        {u.primary_role}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {u.phone}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
